import type {
  ISettingsJob,
  JobApiDto,
  JobAttributeDto,
  JobDefinitionData,
  JobDefinitionDomain
} from "../../types/models/settings.types";
import { AttributeRequirementType, SettingsAttribute } from "./SettingsAttribute";
import { SettingsTask } from "./SettingsTask";

export class SettingsJob implements ISettingsJob {
  id: string | number | null | undefined = undefined;
  rowId: string | number | null = null;
  isJob = true;
  jobDefinitionId: string | number | null = null;
  name: string | null | undefined = undefined;
  description: string | null = null;
  numberOfPositions = 0;
  jobDefinitionDomainId: string | number | JobDefinitionDomain | null = null;
  attributes: SettingsAttribute[] = [];
  regulationTasks: SettingsTask[] = [];
  numberOfCompetencies = 0;
  numberOfRegulationTasks = 0;
  enableJobMatching = false;

  constructor(data?: JobDefinitionData) {
    this.attributes = [];
    this.regulationTasks = [];

    if (data) {
      this.id = data.jobDefinitionId ?? null;
      this.rowId = data.jobDefinitionId ?? null;
      this.isJob = true;
      this.jobDefinitionId = data.jobDefinitionId ?? null;
      this.name = data.name ?? null;
      this.description = data.description ?? null;
      this.numberOfPositions = data.jobs ? data.jobs.length : 0;
      this.jobDefinitionDomainId = data.jobDefinitionDomainId ?? null;
      this.numberOfCompetencies = data.numberOfCompetencies ?? 0;
      this.numberOfRegulationTasks = data.numberOfRegulationTasks ?? 0;

      this.enableJobMatching = data.enableJobMatching ?? false;

      const attrs = data.jobAttributes || data.attributes;
      if (attrs) {
        (attrs as unknown[]).forEach((a: unknown) => {
          const attr = new SettingsAttribute(a as any);
          this.attributes.push(attr);
          this.attributes = this.attributes.concat(attr.childAttributes);
        });
      }
      const listTasks = data.regulationTasks || [];
      if (listTasks) {
        (listTasks as unknown[]).forEach((t: unknown) => {
          const task = new SettingsTask(t);
          this.regulationTasks.push(task);
        });
      }
    }
  }

  isAttributesSame(other: SettingsAttribute[] | null | undefined): boolean {
    if (!other) return false;
    const otherNoChildren = other.filter((a) => !a.isChild);
    if (other.length !== this.attributes.length) return false;

    for (const o of otherNoChildren) {
      const match = this.attributes.find((a) => a.attributeId === o.attributeId && !a.isChild);
      if (!match) return false;

      if (match.requirementType && o.requirementType) {
        if (((match.requirementType as AttributeRequirementType).index) !== ((o.requirementType as AttributeRequirementType).index)) return false;
      }
    }
    return true;
  }

  addAttribute(attr: SettingsAttribute): void {
    this.attributes = [...this.attributes, attr];
  }

  toApiDto(): JobApiDto {
    const attrs: JobAttributeDto[] = [];
    this.attributes
      .filter((a) => a.isChild !== true)
      .forEach((a) => {
        attrs.push({
          attributeDefinitionId: a.attributeDefinitionId as string | number,
          jobAttributeRequirementType:
            a.requirementType && a.isParent !== true ? (a.requirementType as AttributeRequirementType).index : null,
          proficiencyMinimum: a.hasProficiency ? a.proficiencyMinimum || 1 : null,
          isMinimum: a.isMinimum
        });
      });

    // Get domain ID - handle both object and primitive types
    const domainId =
      typeof this.jobDefinitionDomainId === "object" && this.jobDefinitionDomainId !== null
        ? (this.jobDefinitionDomainId as JobDefinitionDomain).id
        : this.jobDefinitionDomainId;

    return {
      regulationTaskIds: this.regulationTasks ? this.regulationTasks.map((r) => r.id) : [],
      jobDefinitionId: this.jobDefinitionId,
      name: this.name,
      description: this.description,
      jobDefinitionDomainId: domainId as string | number | null,
      enableJobMatching: this.enableJobMatching,
      jobAttributes: attrs
    };
  }
}
